import { LinkPlan } from "../../../planBT/types/link.types";
import { NodePlan } from "../../../planBT/types/node.types";
import { Status } from "../../../planBT/types/planBT.types";
import { Connection, GridElement, GeoJSONFeature } from "../types";


export const convertDBDataToGeoJSONFeature = (params: {nodeList: NodePlan[] | null , linkList?: LinkPlan[] | null}) => {
  const features: GeoJSONFeature[] = [];
  
  // Create a map of node IDs to their coordinates for quick lookup
  const nodeCoordinates = new Map<string, [number, number]>();
  
  // Convert nodes to point features and build coordinate map
  if (params.nodeList) {
    params.nodeList.forEach((node) => {
      const coordinates: [number, number] = [node.node_location_lng, node.node_location_lat];
      nodeCoordinates.set(node.node_id, coordinates);
      
      const feature: GeoJSONFeature = {
        type: 'Feature',
        id: node.node_id,
        properties: {
          name: node.node_host_name,
          type: node.node_type,
          node_id: node.node_id,
          created_at: node.node_created_at,
          updated_at: node.node_updated_at,
          hosted_id: node.node_hosted_id,
          account_id: node.node_account_id,
          plan: node.node_plan
        },
        geometry: {
          type: 'Point',
          coordinates: coordinates // Longitude, Latitude
        }
      };
      features.push(feature);
    });
  }

  // Convert links to line features
  if (params.linkList) {
    params.linkList.forEach((link) => {
      // Only create line feature if both nodes exist
      if (link.pl_link_content_node_1 && link.pl_link_content_node_2) {
        const node1Coords = nodeCoordinates.get(link.pl_link_node_1);
        const node2Coords = nodeCoordinates.get(link.pl_link_node_2);
        
        const feature: GeoJSONFeature = {
          type: 'Feature',
          id: link.pl_link_id,
          properties: {
            name: link.pl_link_name,
            link_id: link.pl_link_id,
            node_1: link.pl_link_node_1,
            node_2: link.pl_link_node_2,
            plan: link.pl_link_plan,
            account_id: link.pl_link_account_id,
            created_at: link.pl_link_created_at,
            updated_at: link.pl_link_updated_at
          },
          geometry: {
            type: 'LineString',
            coordinates: [
              node1Coords || [0, 0], // Node 1 coordinates or fallback
              node2Coords || [0, 0]  // Node 2 coordinates or fallback
            ]
          }
        };
        features.push(feature);
      }
    });
  }

  return {
    type: 'FeatureCollection' as const,
    features
  };
}

// Convert GPS coordinates to canvas coordinates
// This function maps GPS coordinates to a reasonable canvas space (0-1200 for width, 0-800 for height)
export const gpsToCanvas = (lat: number, lng: number, bounds?: {minLat: number, maxLat: number, minLng: number, maxLng: number}) => {
  // Default canvas dimensions
  const CANVAS_WIDTH = 1200;
  const CANVAS_HEIGHT = 800;
  const PADDING = 50; // Padding from edges
  
  if (bounds) {
    // If bounds are provided, map relative to those bounds
    const latRange = bounds.maxLat - bounds.minLat;
    const lngRange = bounds.maxLng - bounds.minLng;
    
    // Handle single point case
    if (latRange === 0 && lngRange === 0) {
      return { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 };
    }
    
    // Map to canvas with padding
    const x = PADDING + ((lng - bounds.minLng) / lngRange) * (CANVAS_WIDTH - 2 * PADDING);
    const y = PADDING + ((bounds.maxLat - lat) / latRange) * (CANVAS_HEIGHT - 2 * PADDING); // Invert Y for SVG
    
    return { x, y };
  } else {
    // For single conversion without bounds, use a simple scaling
    // This is a fallback for individual conversions
    return {
      x: (lng + 180) * (CANVAS_WIDTH / 360), // Normalize longitude from -180/180 to 0/CANVAS_WIDTH
      y: (90 - lat) * (CANVAS_HEIGHT / 180)   // Normalize latitude from -90/90 to 0/CANVAS_HEIGHT (inverted)
    };
  }
};

// Convert canvas coordinates to GPS coordinates (inverse function)
// This function maps canvas coordinates back to GPS coordinates
export const canvasToGps = (x: number, y: number, bounds?: {minLat: number, maxLat: number, minLng: number, maxLng: number}) => {
  // Default canvas dimensions
  const CANVAS_WIDTH = 1200;
  const CANVAS_HEIGHT = 800;
  const PADDING = 50; // Padding from edges
  
  if (bounds) {
    // If bounds are provided, map relative to those bounds
    const latRange = bounds.maxLat - bounds.minLat;
    const lngRange = bounds.maxLng - bounds.minLng;
    
    // Handle single point case
    if (latRange === 0 && lngRange === 0) {
      return { latitude: bounds.minLat, longitude: bounds.minLng };
    }
    
    // Convert from canvas coordinates back to GPS
    const normalizedX = (x - PADDING) / (CANVAS_WIDTH - 2 * PADDING);
    const normalizedY = (y - PADDING) / (CANVAS_HEIGHT - 2 * PADDING);
    
    const longitude = bounds.minLng + (normalizedX * lngRange);
    const latitude = bounds.maxLat - (normalizedY * latRange); // Invert Y back from SVG
    
    return { latitude, longitude };
  } else {
    // For single conversion without bounds, use simple scaling (inverse of gpsToCanvas)
    const longitude = (x / CANVAS_WIDTH) * 360 - 180; // Convert from 0/CANVAS_WIDTH to -180/180
    const latitude = 90 - (y / CANVAS_HEIGHT) * 180;  // Convert from 0/CANVAS_HEIGHT to 90/-90 (invert)
    
    return { latitude, longitude };
  }
};

export const convertDBDataNodeToGridElement = ( node: NodePlan, allNodes?: NodePlan[] ) : GridElement=> {
  // Map node type to grid element type
  const getElementType = (nodeType: string): GridElement['type'] => {
    switch (nodeType) {
      case 'POLE':
        return 'pole';
      case 'TRANSFORMER':
        return 'transformer';
      case 'METER':
        return 'meter';
      case 'CENTRALPLANT':
        return 'power-plant';
      case 'CLIENT': // Legacy support
        return 'meter';
      default:
        return 'pole'; // Default fallback
    }
  };

  // Get default properties based on element type
  const getDefaultProperties = (type: GridElement['type'], node?: NodePlan) => {
    const baseProperties = {
      pole: {},
      meter: { consumption: 2.5, voltage: 230 },
      'power-plant': { power: 50, voltage: 400, efficiency: 85 },
      transformer: { power: 25, voltage: 400 }
    };

    const properties = baseProperties[type] || {};
    
    // Extract specific properties from node_host if available
    if (node?.node_host && typeof node.node_host === 'object') {
      const host = node.node_host as any;
      
      // For meters, extract mtr_number
      if (type === 'meter' && host.mtr_number) {
        return { ...properties, mtr_number: String(host.mtr_number) };
      }
      
      // For poles, extract pole_type and pole_number.
      // pole_number is stored separately in V1 plans (pole_number ≠ pole_name) so we
      // carry it into properties to enable robust pole matching on import.
      if (type === 'pole') {
        const poleProps: Record<string, any> = { ...properties };
        if (host.pole_type) poleProps.pole_type = host.pole_type;
        if (host.pole_number != null && host.pole_number !== '') {
          poleProps.pole_number = String(host.pole_number);
        }
        return poleProps;
      }
    }
    
    return properties;
  };

  // Extract status from node_host if available
  const getStatusFromNodeHost = (node: NodePlan): Status => {
    if (node.node_host && typeof node.node_host === 'object') {
      const host = node.node_host as any;
      
      // Check for different status property names based on host type
      const statusValue = host.pole_status || host.trnsf_status || host.mtr_status || host.cntrlp_status || host.str_light_status;
      
      if (statusValue && ['ACTIVE', 'INACTIVE', 'MAINTENANCE', 'OFFLINE'].includes(statusValue)) {
        return statusValue as Status;
      }
    }
    return "ACTIVE"; // Default status
  };

  const elementType = getElementType(node.node_type);
  
  // Calculate bounds if all nodes are provided
  let bounds;
  if (allNodes && allNodes.length > 1) {
    const lats = allNodes.map(n => n.node_location_lat);
    const lngs = allNodes.map(n => n.node_location_lng);
    bounds = {
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats),
      minLng: Math.min(...lngs),
      maxLng: Math.max(...lngs)
    };
  }
  
  const { x, y } = gpsToCanvas(node.node_location_lat, node.node_location_lng, bounds);

  const defaultProperties = getDefaultProperties(elementType, node);
  const meterNumber = elementType === 'meter' && 'mtr_number' in defaultProperties
    ? String((defaultProperties as any).mtr_number)
    : undefined;

  // Numéro affiché pour un poteau: extrait du pole_number (ou du nom en repli).
  // Le pole_number stocké est souvent le libellé complet ("Poteau 126"); on en
  // tire la première séquence de chiffres ("126"), cohérent avec le rapprochement
  // compteur->poteau backend (poleNumberOf = premier nombre). Sans le mapper ici,
  // le champ "Numéro" du panneau resterait "Non défini" même pour "Poteau 126".
  let poleNumber: string | undefined;
  if (elementType === 'pole') {
    const rawPoleNumber = (defaultProperties as any).pole_number ?? node.node_host_name;
    if (rawPoleNumber != null && String(rawPoleNumber) !== '') {
      const match = String(rawPoleNumber).match(/\d+/);
      poleNumber = match ? match[0] : String(rawPoleNumber);
    }
  }

  // Extract isJouly, boxAssoId from node_host for meter elements
  let isJouly: boolean | undefined;
  let boxAssoId: string | undefined;
  if (elementType === 'meter' && node.node_host && typeof node.node_host === 'object') {
    const host = node.node_host as any;
    if (host.mtr_is_jouly) isJouly = true;
    if (host.mtr_box_asso_id) boxAssoId = host.mtr_box_asso_id;
  }

  return {
    id: node.node_id, // Use original ID without prefix
    type: elementType,
    nodeId: node.node_id,
    hostId: node.node_hosted_id,
    // Use converted coordinates
    x: x,
    y: y,
    name: node.node_host_name,
    number: meterNumber ?? poleNumber,
    properties: defaultProperties,
    status: getStatusFromNodeHost(node),
    // Add lights array for poles
    lights: elementType === 'pole' ? [] : undefined,
    // Conserver les coordonnées GPS originales
    gpsCoordinates: {
      latitude: node.node_location_lat,
      longitude: node.node_location_lng
    },
    // Flags de synchronisation 8000
    isJouly,
    boxAssoId,
  };
}

export const convertDBDataLinkToConnection = ( link: LinkPlan ) : Connection=> {
  return {
    id: link.pl_link_id, // Use original ID without prefix
    name: link.pl_link_name || link.pl_link_id, // Récupérer le nom depuis pl_link_name
    fromId: link.pl_link_node_1, // Use original node IDs
    toId: link.pl_link_node_2, // Use original node IDs
    type: 'power-line',
    voltage: 400, // Default voltage
    resistance: 0.2, // Default resistance
    length: 100, // Default length - should be calculated from node positions
    status: link.pl_link_status || "ACTIVE" // Use status from database or default to ACTIVE
  };
}

/**
 * Calculate distance between two geographical points using Haversine formula
 * Returns distance in meters
 */
export const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

/**
 * Recalculates the length of a connection based on the current positions of its elements
 * @param connection - The connection to recalculate
 * @param elements - Array of all elements to find the connection's endpoints
 * @returns The calculated length in meters, or 100 if calculation fails
 */
export const recalculateConnectionLength = (connection: Connection, elements: GridElement[]): number => {
  const fromElement = elements.find(el => el.id === connection.fromId);
  const toElement = elements.find(el => el.id === connection.toId);

  if (!fromElement || !toElement) {
    console.warn(`⚠️ Cannot recalculate length for connection ${connection.id}: missing elements`);
    return 100; // Default length
  }

  const fromLat = fromElement.gpsCoordinates?.latitude || fromElement.y / 100000;
  const fromLng = fromElement.gpsCoordinates?.longitude || fromElement.x / 100000;
  const toLat = toElement.gpsCoordinates?.latitude || toElement.y / 100000;
  const toLng = toElement.gpsCoordinates?.longitude || toElement.x / 100000;

  return calculateDistance(fromLat, fromLng, toLat, toLng);
};

/**
 * Recalculates lengths for all connections based on current element positions
 * @param connections - Array of connections to recalculate
 * @param elements - Array of all elements
 * @returns Array of connections with updated lengths
 */
export const recalculateAllConnectionLengths = (connections: Connection[], elements: GridElement[]): Connection[] => {
  return connections.map(connection => ({
    ...connection,
    length: recalculateConnectionLength(connection, elements)
  }));
};

/**
 * Converts complete database data (nodes and links) to grid editor format
 * This is a convenience function that combines node and link conversion
 */
export const convertDBDataToGridData = (params: {nodeList: NodePlan[] | null, linkList?: LinkPlan[] | null}) => {
  const elements: GridElement[] = [];
  const connections: Connection[] = [];
  
  // Create a map of node IDs to their coordinates for distance calculation
  const nodePositions = new Map<string, {lat: number, lng: number}>();

  // Convert nodes to elements and build position map
  if (params.nodeList) {
    params.nodeList.forEach((node) => {
      elements.push(convertDBDataNodeToGridElement(node, params.nodeList || undefined));
      nodePositions.set(node.node_id, {
        lat: node.node_location_lat,
        lng: node.node_location_lng
      });
    });
  }

  // Convert links to connections with calculated length
  if (params.linkList) {
    params.linkList.forEach((link) => {
      const node1Pos = nodePositions.get(link.pl_link_node_1);
      const node2Pos = nodePositions.get(link.pl_link_node_2);
      
      let calculatedLength = 100; // Default length
      if (node1Pos && node2Pos) {
        calculatedLength = calculateDistance(
          node1Pos.lat, node1Pos.lng,
          node2Pos.lat, node2Pos.lng
        );
      }
      
      const connection: Connection = {
        id: link.pl_link_id,
        name: link.pl_link_name || link.pl_link_id, // Récupérer le nom depuis pl_link_name
        fromId: link.pl_link_node_1,
        toId: link.pl_link_node_2,
        type: 'power-line',
        voltage: 400, // Default voltage
        resistance: 0.2, // Default resistance
        length: calculatedLength,
        status: link.pl_link_status || "ACTIVE" // Use status from database or default to ACTIVE
      };
      
      connections.push(connection);
    });
  }

  return {
    elements,
    connections
  };
}